import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { invariant } from '@utils/invariant';
import { trackEvent } from '@utils/track-event';

import { Button } from '../../../components/button/Button';
import { Dialog } from '../../../components/dialog/Dialog';
import { Pill } from '../../../components/pill/Pill';
import { DelayedSpinner } from '../../../components/Spinner';
import { ITableHeader, Table } from '../../../components/table/Table';
import { formatDateTime } from '../../../utils/date';
import { getDisplayError } from '../../../utils/get-display-error';
import { useSpotMarketOffers, pushToSpotMarket, withdrawSpotMarketOffer, useSpotPricingProviders } from '../models';
import type { SpotMarketOffer } from '../endpoints/SpotMarketOffersEndpoint.types';
import type { PillColor } from '../../../components/pill/Pill';
import { ErrorState } from '../../../components/error-state/ErrorState';

type SpotPricingProvider = 'teleroute' | 'b2pconnect';

const STATUS_COLOR: Record<string, PillColor> = {
  Active: 'positive',
  Withdrawn: 'neutral',
  Expired: 'primary',
  Failed: 'negative',
};

const STATUS_LABEL: Record<string, string> = {
  Active: 'Actief',
  Withdrawn: 'Ingetrokken',
  Expired: 'Verlopen',
  Failed: 'Mislukt',
};

const PROVIDER_LABEL: Record<string, string> = {
  teleroute: 'Teleroute',
  b2pconnect: 'B2PConnect',
};

const TABLE_HEADERS: ITableHeader[] = [
  { id: 'provider', name: 'Aanbieder' },
  { id: 'status', name: 'Status' },
  { id: 'pushedAt', name: 'Verstuurd op' },
  { id: 'pushedBy', name: 'Verstuurd door' },
  { id: 'withdrawnAt', name: 'Ingetrokken op' },
  { id: 'externalId', name: 'Extern ID' },
  { id: 'actions', name: '' },
];

export const SpotMarketOffersPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  invariant(orderId);

  const { data, error, mutate, isLoading } = useSpotMarketOffers(orderId);
  const { data: providersData, error: providersError, mutate: mutateProviders } = useSpotPricingProviders();
  const [isPushDialogOpen, setIsPushDialogOpen] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState<Set<SpotPricingProvider>>(new Set());
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);
  const [errorDetailOffer, setErrorDetailOffer] = useState<SpotMarketOffer | null>(null);

  const offers = data?.offers ?? [];

  const activeProviders = useMemo<Set<SpotPricingProvider>>(() => {
    return new Set(offers.filter((o) => o.status === 'Active').map((o) => o.provider as SpotPricingProvider));
  }, [offers]);

  const enabledProviders = useMemo<SpotPricingProvider[]>(() => {
    return (providersData?.providers ?? []).filter((p) => p.enabled).map((p) => p.name as SpotPricingProvider);
  }, [providersData]);

  const availableProviders = useMemo<SpotPricingProvider[]>(() => {
    return enabledProviders.filter((p) => !activeProviders.has(p));
  }, [enabledProviders, activeProviders]);

  const openPushDialog = () => {
    setSelectedProviders(new Set(availableProviders));
    setIsPushDialogOpen(true);
  };

  const toggleProvider = (provider: SpotPricingProvider) => {
    setSelectedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(provider)) next.delete(provider);
      else next.add(provider);
      return next;
    });
  };

  const handlePush = async () => {
    if (selectedProviders.size === 0) return;
    setIsPushing(true);
    try {
      const providers = [...selectedProviders];
      const result = await pushToSpotMarket(orderId, { providers });

      const failed = result.results.filter((r) => !r.success);
      const succeeded = result.results.filter((r) => r.success);

      trackEvent('spot_market_offer_pushed', {
        order_id: orderId,
        providers,
        success_count: succeeded.length,
        failed_count: failed.length,
      });

      if (failed.length > 0) {
        toast.error(
          `Fout bij ${failed.map((f) => PROVIDER_LABEL[f.provider] ?? f.provider).join(', ')}: ${failed[0]!.errorMessage}`,
        );
      }
      if (succeeded.length > 0) {
        toast.success(
          `Order naar ${succeeded.map((s) => PROVIDER_LABEL[s.provider] ?? s.provider).join(', ')} gestuurd`,
        );
      }

      setIsPushDialogOpen(false);
      await mutate();
    } catch (err) {
      toast.error(getDisplayError(err));
    } finally {
      setIsPushing(false);
    }
  };

  const handleWithdraw = async (offer: SpotMarketOffer) => {
    setWithdrawingId(offer.id);
    try {
      const result = await withdrawSpotMarketOffer(orderId, offer.id);
      trackEvent('spot_market_offer_withdrawn', {
        order_id: orderId,
        offer_id: offer.id,
        provider: offer.provider,
      });
      if (result.warning) {
        toast.error(result.warning);
      } else {
        toast.success('Aanbod ingetrokken');
      }
      await mutate();
    } catch (err) {
      toast.error(getDisplayError(err));
    } finally {
      setWithdrawingId(null);
    }
  };

  if (isLoading) {
    return <DelayedSpinner className="card p-8 flex justify-center" />;
  }

  if (error || providersError) {
    return (
      <ErrorState
        error={error ?? providersError}
        onRetry={() => Promise.all([mutate(), mutateProviders()])}
        variant="section"
      />
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button color="primary" onTrigger={openPushDialog} isDisabled={availableProviders.length === 0}>
          Naar spotmarkt sturen
        </Button>
      </div>

      {offers.length === 0 ? (
        <div className="card p-8 text-center text-fg-muted">Nog geen aanbiedingen op de spotmarkt geplaatst.</div>
      ) : (
        <div className="w-full overflow-auto">
          <Table<SpotMarketOffer>
            idKey="id"
            headers={TABLE_HEADERS}
            data={offers}
            mapData={(offer) => [
              PROVIDER_LABEL[offer.provider] ?? offer.provider,
              <Pill color={STATUS_COLOR[offer.status] ?? 'neutral'} size="sm">
                {STATUS_LABEL[offer.status] ?? offer.status}
              </Pill>,
              formatDateTime(offer.pushedAt),
              offer.pushedByName ?? '-',
              offer.withdrawnAt ? formatDateTime(offer.withdrawnAt) : '-',
              <span className="text-xs font-mono">{offer.externalOfferId ?? '-'}</span>,
              <>
                {offer.status === 'Active' && (
                  <Button
                    color="danger"
                    onTrigger={() => handleWithdraw(offer)}
                    isLoading={withdrawingId === offer.id}
                    isDisabled={withdrawingId !== null}
                  >
                    Intrekken
                  </Button>
                )}
                {offer.status === 'Failed' && offer.errorMessage && (
                  <Button color="default" onTrigger={() => setErrorDetailOffer(offer)}>
                    Details
                  </Button>
                )}
              </>,
            ]}
          />
        </div>
      )}

      <Dialog isOpen={isPushDialogOpen} onClose={() => setIsPushDialogOpen(false)} title="Stuur naar spotmarkt">
        <div className="space-y-4">
          <p>Selecteer de spotmarkt-aanbieders waar je de order op wil plaatsen:</p>

          <div className="flex flex-col gap-2">
            {enabledProviders.map((provider) => {
              const alreadyActive = activeProviders.has(provider);
              return (
                <label
                  key={provider}
                  className={`flex items-center gap-2 ${alreadyActive ? 'text-fg-muted' : 'cursor-pointer'}`}
                >
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={selectedProviders.has(provider)}
                    onChange={() => toggleProvider(provider)}
                    disabled={isPushing || alreadyActive}
                  />
                  <span>
                    {PROVIDER_LABEL[provider] ?? provider}
                    {alreadyActive && <span className="ml-2 text-xs">(al actief aanbod — trek eerst in)</span>}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="flex justify-end gap-2">
            <Button color="default" onTrigger={() => setIsPushDialogOpen(false)} isDisabled={isPushing}>
              Annuleer
            </Button>
            <Button
              color="primary"
              onTrigger={handlePush}
              isLoading={isPushing}
              isDisabled={isPushing || selectedProviders.size === 0}
            >
              Verstuur
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog isOpen={errorDetailOffer !== null} onClose={() => setErrorDetailOffer(null)} title="Foutdetails">
        {errorDetailOffer && (
          <div className="space-y-4">
            <div>
              <div className="text-sm font-medium text-fg-muted mb-1">Aanbieder</div>
              <div>{PROVIDER_LABEL[errorDetailOffer.provider] ?? errorDetailOffer.provider}</div>
            </div>

            <div>
              <div className="text-sm font-medium text-fg-muted mb-1">Foutmelding</div>
              <div className="text-negative-text">{errorDetailOffer.errorMessage}</div>
            </div>

            <div className="flex justify-end">
              <Button color="default" onTrigger={() => setErrorDetailOffer(null)}>
                Sluiten
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};
